import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { formatRoute } from '../../app/router';
import { PageHeader } from '../../design-system/primitives';
import { Empty, errorView, Loading } from '../../states/States';
import { useLoad } from '../../views/useLoad';
import { AppServicesContext, useAppServices } from '../../views/ViewContext';
import { studioChrome } from '../editor/studio-theme';
import { StudioShell } from '../editor/StudioShell';
import { DeveloperModeContext } from '../editor/developer-mode';
import type { SchemaNode, SchemaRegistry } from '../editor/schema-properties';
import { FormsStudioApi, ORIGIN_LABELS, templateTitle, type TemplateDetail, type TemplateListItem } from './forms-api';
import { formHistoryPort } from './form-history';
import { formInspector } from './form-inspector';
import { formCanvasProfile, rulesOf } from './form-profile';
import { AssistantMarks, assistantPanel } from './v2/assistant';
import { LayoutChooser, stepsPanel } from './LayoutChooser';
import type { EditorRegistry } from './layout-registry';
import type { StudioDraftResult } from '@ayumia/contracts';
import type { Template } from './forms-model';
import { EMPTY_CATALOG, type StudioCatalog } from './v2/library';
import type { TranslatePort } from './v2/locales';
import './v2/v2.css';
import './forms.css';
import { MediaClientContext } from '../../work/form-v2/media-links';

/**
 * Studio › Formulaires (ADR-141 §2, tranche 3) : la liste des gabarits du tenant avec leur couche, leur
 * version et leur statut, puis l'éditeur d'un gabarit sur le socle commun du Studio. Un gabarit de Pack
 * ne se modifie jamais en place : l'éditer prépare sa surcharge par le client, publiée par `ModelChange`.
 * Réservé à qui lit la configuration ; proposer et approuver sont des droits distincts, vérifiés au serveur.
 */

/** Registre des contrats par `$id`, chargé à l'ouverture de l'éditeur seulement (chunk séparé). */
async function loadRegistry(): Promise<SchemaRegistry> {
  const { schemas } = await import('@ayumia/contracts/schemas');
  return Object.fromEntries(Object.values(schemas as Record<string, SchemaNode>).filter((schema) => typeof schema['$id'] === 'string').map((schema) => [schema['$id'] as string, schema]));
}

export function FormsStudioPage({ artifactId, onOpen, onBack }: { artifactId?: string; onOpen: (artifactId: string) => void; onBack: () => void }) {
  const services = useAppServices();
  const api = useMemo(() => new FormsStudioApi(services.client), [services.client]);
  return artifactId === undefined ? <TemplateList api={api} onOpen={onOpen} /> : <TemplateEditor api={api} artifactId={artifactId} onBack={onBack} onOpen={onOpen} />;
}

function statusLabel(item: TemplateListItem): string {
  if (item.overridden_by !== undefined) return `Surchargé par ${ORIGIN_LABELS[item.overridden_by.origin] ?? item.overridden_by.origin}`;
  return item.status === 'published' ? 'Publié, actif' : item.status;
}

function TemplateList({ api, onOpen }: { api: FormsStudioApi; onOpen: (artifactId: string) => void }) {
  const [loaded, reload] = useLoad('forms-studio-list', () => api.list());
  const [search, setSearch] = useState('');
  return (
    <section className="ay-view ay-forms" aria-labelledby="ay-forms-title">
      <PageHeader titleId="ay-forms-title" title="Formulaires" subtitle="Les gabarits des tâches, par couche. Un gabarit de Pack se surcharge par une version du client, jamais en place ; toute publication passe par une proposition, approuvée par une seconde personne si elle change la structure." />
      {loaded.status === 'loading' && <Loading label="Lecture des gabarits…" />}
      {loaded.status === 'error' && errorView(loaded.error, reload)}
      {loaded.status === 'ready' && (
        <>
          <label className="ay-forms__search">
            <span className="ay-field__label">Rechercher</span>
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nom du formulaire" />
          </label>
          <TemplateTable items={loaded.value.filter((item) => `${templateTitle(item)} ${item.form ?? ''} ${item.artifact_id}`.toLocaleLowerCase('fr').includes(search.trim().toLocaleLowerCase('fr')))} onOpen={onOpen} />
        </>
      )}
    </section>
  );
}

export function TemplateTable({ items, onOpen }: { items: readonly TemplateListItem[]; onOpen: (artifactId: string) => void }) {
  if (items.length === 0) return <Empty label="Aucun gabarit." />;
  return (
    <table className="ay-table ay-forms__table">
      <thead>
        <tr>
          <th scope="col">Formulaire</th>
          <th scope="col">Couche</th>
          <th scope="col">Version</th>
          <th scope="col">Statut</th>
          <th scope="col"><span className="ay-visually-hidden">Action</span></th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.artifact_id} className={item.overridden_by !== undefined ? 'is-overridden' : undefined}>
            <td>
              <strong>{templateTitle(item)}</strong>
            </td>
            <td><span className="ay-badge">{ORIGIN_LABELS[item.origin] ?? item.origin}</span></td>
            <td>
              {item.artifact_version}
              {item.pending_versions.length > 0 && <div className="ay-text ay-text--muted">en attente : {item.pending_versions.join(', ')}</div>}
            </td>
            <td>{statusLabel(item)}</td>
            <td>
              {/* Un lien : clic dans la page ; clic du milieu ou Ctrl+clic dans un nouvel onglet (même session). */}
              <a
                className="ay-button ay-button--secondary"
                href={formatRoute({ kind: 'forms', artifact_id: item.artifact_id })}
                onClick={(event) => {
                  if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey) return;
                  event.preventDefault();
                  onOpen(item.artifact_id);
                }}
                aria-label={`${item.editable ? 'Éditer' : 'Surcharger'} ${templateTitle(item)}`}
              >
                {item.editable ? 'Éditer' : item.overridden_by !== undefined ? 'Voir' : 'Surcharger'}
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TemplateEditor({ api, artifactId, onBack, onOpen }: { api: FormsStudioApi; artifactId: string; onBack: () => void; onOpen: (artifactId: string) => void }) {
  const services = useAppServices();
  const [loaded, reload] = useLoad(`forms-studio:${artifactId}`, async () => {
    const detail = await api.get(artifactId);
    // Le brouillon de l'auteur au serveur se reprend à l'ouverture (ADR-146 addendum (11)).
    return { detail, registry: await loadRegistry(), editor: await api.registry(), draft: await api.draft(artifactId) };
  });
  return (
    <section className="ay-studio-page" aria-label="Éditeur de formulaire">
      {loaded.status === 'loading' && <Loading label="Ouverture du gabarit…" />}
      {loaded.status === 'error' && errorView(loaded.error, reload)}
      {loaded.status === 'ready' && <ReadyEditor detail={loaded.value.detail} registry={loaded.value.registry} editorRegistry={loaded.value.editor} savedDraft={loaded.value.draft} api={api} me={typeof services.context['identity_id'] === 'string' ? services.context['identity_id'] : undefined} onOpen={onOpen} onBack={onBack} onPublished={reload} />}
    </section>
  );
}

/** Chrome sombre des studios : les jetons du thème en mode `dark` (ADR-146 §3.3), l'aperçu reste aux couleurs de la cible. */
const STUDIO_CHROME = studioChrome();

export function ReadyEditor({ detail, registry, editorRegistry, savedDraft = null, api, me, onOpen, onBack, onPublished }: { detail: TemplateDetail; registry: SchemaRegistry; editorRegistry: EditorRegistry; savedDraft?: StudioDraftResult['draft']; api: FormsStudioApi; me: string | undefined; onOpen: (artifactId: string) => void; onBack?: () => void; onPublished: () => void }) {
  // Un faux d'API de test peut ne pas porter le brouillon : l'éditeur garde alors son brouillon en mémoire.
  // Marques des opérations de l'assistant appliquées : elles voyagent avec chaque enregistrement du brouillon.
  const marks = useMemo(() => new AssistantMarks(), []);
  const drafts = useMemo(() => (typeof (api as Partial<FormsStudioApi>).drafts === 'function' ? api.drafts(detail, marks) : undefined), [api, detail, marks]);
  const assistant = useMemo(() => (typeof (api as Partial<FormsStudioApi>).assistant === 'function' ? api.assistant(detail) : undefined), [api, detail]);
  const resumed = savedDraft === null ? null : savedDraft;
  const port = useMemo(() => api.governance(detail, me), [api, detail, me]);
  const [developer, setDeveloper] = useState(false);
  const [choosing, setChoosing] = useState<{ document: Template; apply: (next: Template) => void } | null>(null);
  // Bibliothèque de l'éditeur v2 (modèle, préréglages, couches) : lue après l'ouverture, sans la retarder.
  const [catalog, setCatalog] = useState<StudioCatalog>(EMPTY_CATALOG);
  const catalogRef = useRef(catalog);
  catalogRef.current = catalog;
  useEffect(() => {
    if (typeof (api as Partial<FormsStudioApi>).catalog !== 'function') return undefined;
    let alive = true;
    api.catalog().then((loaded) => alive && setCatalog(loaded), () => undefined);
    return () => {
      alive = false;
    };
  }, [api]);
  const presets = useMemo(() => (typeof (api as Partial<FormsStudioApi>).presets === 'function' ? api.presets(() => catalogRef.current) : undefined), [api]);
  // Traduction suggérée : branchée seulement si le tenant la sert (sinon « Suggérer » reste grisé avec son motif).
  const [translate, setTranslate] = useState<TranslatePort | undefined>(undefined);
  useEffect(() => {
    if (typeof (api as Partial<FormsStudioApi>).translations !== 'function') return undefined;
    let alive = true;
    const port = api.translations(detail);
    port.availability().then((availability) => alive && availability.available && setTranslate(port), () => undefined);
    return () => {
      alive = false;
    };
  }, [api, detail]);
  // Langues ajoutées dans la session (« Ajouter une langue ») : elles n'existent au gabarit qu'une fois traduites.
  const [addedLocales, setAddedLocales] = useState<string[]>([]);
  const services = useContext(AppServicesContext);
  const profile = useMemo(() => {
    const open = (document: Template, apply: (next: Template) => void): void => setChoosing({ document, apply });
    const inspector = formInspector({
      registry: editorRegistry,
      schemas: registry,
      rules: detail.rules,
      developer,
      onChooseLayout: open,
      catalog,
      ...(presets === undefined ? {} : { presets }),
      ...(services?.client === undefined ? {} : { client: services.client }),
      addedLocales,
      onAddLocale: (locale) => setAddedLocales((current) => (current.includes(locale) ? current : [...current, locale])),
      ...(translate === undefined ? {} : { translate }),
    });
    const panels = [stepsPanel(editorRegistry, open), ...(assistant === undefined ? [] : [assistantPanel(assistant, marks)])];
    return formCanvasProfile(editorRegistry, inspector, panels, rulesOf(detail.rules), catalog, { addedLocales });
  }, [editorRegistry, registry, detail.rules, developer, catalog, presets, services?.client, addedLocales, assistant, marks, translate]);
  const history = useMemo(() => formHistoryPort({ versions: () => api.versions(detail.artifact_id), body: (artifactId, version) => api.version(artifactId, version) }), [api, detail.artifact_id]);
  const isPack = detail.origin !== 'customer';
  const origin = ORIGIN_LABELS[detail.origin] ?? 'Pack';
  // La couche est une pastille ; son explication s'ouvre depuis la pastille, jamais en bandeau permanent.
  const layer = isPack ? `Gabarit du ${origin} · ${detail.artifact_version}` : `Couche client · ${detail.artifact_version}`;
  const layerHelp = isPack
    ? `Vos modifications forment un brouillon de surcharge par le client. Le gabarit du ${origin} n’est jamais modifié.`
    : 'Gabarit du client : chaque publication crée une nouvelle version ; les tâches en cours gardent la leur.';
  // Avertissements réels seulement : une pastille cliquable dans la barre, le détail s'ouvre au clic.
  const stale = resumed !== null && resumed.base_artifact_version !== detail.artifact_version;
  const replaced = isPack && detail.overridden_by !== undefined;
  // La première publication d'un gabarit du Pack crée la version du client : l'éditeur passe sur elle, celle que
  // la prochaine modification devra changer (sinon : « Le client a déjà sa version de ce formulaire »).
  const afterPublish = (): void => {
    if (!isPack) return onPublished();
    api.get(detail.artifact_id).then(
      (fresh) => (fresh.overridden_by === undefined ? onPublished() : onOpen(fresh.overridden_by.artifact_id)),
      () => onPublished(),
    );
  };
  const notice = stale && resumed !== null ? (
    <p className="ay-forms__notice" role="note">Brouillon repris : il a été commencé sur la version {resumed.base_artifact_version} ; la version publiée est maintenant la {detail.artifact_version}. Comparez-les dans « Historique » avant de publier.</p>
  ) : replaced ? (
    <span className="ay-forms__notice" role="note">
      <span>Ce gabarit du {origin} est remplacé par la version du client : c’est elle qui s’applique.</span>
      <button type="button" className="ay-button ay-button--secondary" onClick={() => onOpen(detail.overridden_by!.artifact_id)}>Ouvrir la version du client</button>
    </span>
  ) : undefined;
  return (
    <DeveloperModeContext.Provider value={developer}>
      <MediaClientContext.Provider value={services?.client}>
      <StudioShell<Template>
        key={`${detail.artifact_id}@${detail.artifact_version}`}
        title={templateTitle({ label: (resumed === null ? detail.body.label : (resumed.body as { label?: unknown }).label) as Record<string, string>, artifact_id: detail.artifact_id })}
        layer={layer}
        layerHelp={layerHelp}
        base={detail.body}
        initial={resumed === null ? detail.body : (resumed.body as unknown as Template)}
        {...(drafts === undefined ? {} : { drafts })}
        {...(resumed === null ? {} : { savedDraft: { version: resumed.draft_version, savedAt: new Date(resumed.saved_at as unknown as string).toISOString() } })}
        profile={profile}
        port={port}
        history={history}
        onPublished={afterPublish}
        chrome={STUDIO_CHROME}
        {...(onBack === undefined ? {} : { onClose: onBack })}
        {...(notice === undefined ? {} : { notice, noticeLabel: stale ? 'Brouillon d’une autre version' : 'Remplacé par le client' })}
        developer={{ on: developer, onChange: setDeveloper }}
      />
      {choosing !== null && (
        <LayoutChooser open registry={editorRegistry} template={choosing.document} onClose={() => setChoosing(null)} onApply={(next) => { choosing.apply(next); setChoosing(null); }} />
      )}
      </MediaClientContext.Provider>
    </DeveloperModeContext.Provider>
  );
}
